import { DecimalPipe, NgClass } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { RemoveAtSignPipe } from '@core/pipes/remove-at-sign.pipe';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EPlatform } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '@shared/components/base.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SkeletonLoaderComponent } from '@shared/components/skeleton-loader/skeleton-loader.component';
import { ISkeletonColumn } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { camelCase } from 'lodash';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter } from 'rxjs';
import { AddPlatformDialogComponent } from '../creator-details/add-platform-dialog/add-platform-dialog.component';
import { IAddPlatformDialogData } from '../creator-details/add-platform-dialog/add-platform-dialog.interface';
import { IChannel, IChannelAnalytics, ICreator, ICreatorChannelAnalyticsResponse } from '../creators.interface';
import { ISetChannelAsDefaultEvent, ISkeletonColumnWidths } from './creator-platform-list.interface';
import { CreatorPlatformListStore } from './creator-platform-list.store';

@Component({
  imports: [
    HasPermissionDirective,
    MatButtonModule,
    MatInputModule,
    MatMenuModule,
    MatTableModule,
    TranslateModule,
    MatDialogModule,
    SocialBadgeComponent,
    IconBadgeComponent,
    ReactiveFormsModule,
    DotsSpinnerComponent,
    RemoveAtSignPipe,
    SkeletonLoaderComponent,
    DecimalPipe,
    MatTooltipModule,
    IconComponent,
    NgxSkeletonLoaderModule,
    NgClass,
  ],
  providers: [CreatorPlatformListStore],
  selector: 'app-creator-platform-list',
  styleUrl: './creator-platform-list.component.scss',
  templateUrl: './creator-platform-list.component.html',
})
export class CreatorPlatformListComponent extends BaseComponent {
  public readonly creator = input.required<ICreator>();

  public readonly isEditingName = input<boolean>(false);
  public readonly iconFormValue = input<string | TNil>(null);
  public readonly loading = input<boolean>(false);
  public readonly adminMode = input<boolean>(false);
  public readonly creatorMode = input<boolean>(false);

  public readonly channelsChanged = output<IChannel[]>();
  public readonly channelImageSelected = output<string>();
  public readonly creatorChanged = output<ICreator>();
  public readonly channelSelectedAsDefault = output<ISetChannelAsDefaultEvent>();

  public readonly dataSource = signal<MatTableDataSource<IChannel>>(new MatTableDataSource<IChannel>([]));
  public readonly totalFollowers = signal<number>(0);
  public readonly isValidSignal = signal<boolean>(false);

  public readonly searchControl = new FormControl<string>('');
  public readonly searchValue = signal<string>('');

  public readonly hasCreatorAuthenticatePermission = computed(() =>
    this.permissionService.hasPermission(this.EPermissions.creatorAuthenticate),
  );

  public readonly hasCreatorFollowersReadPermission = computed(() =>
    this.permissionService.hasPermission(this.EPermissions.creatorFollowersRead),
  );

  public readonly displayedColumns = computed(() => {
    const cols = ['platform', 'channel'];

    if (this.hasCreatorFollowersReadPermission()) {
      cols.push('followers');
    }

    if (this.hasCreatorAuthenticatePermission()) {
      cols.push('authentication');
    }

    cols.push('actions');

    return cols;
  });

  private readonly iconCol = (className: string): ISkeletonColumn => ({
    borderRadius: '8px',
    circleSize: '24px',
    className,
    columnPadding: '0 16px',
    gap: '0.5rem',
    hasText: true,
    height: '20px',
    rowPadding: '16px 0',
    textWidth: '120px',
    type: 'icon',
    width: '80px',
  });

  private readonly textCol = (className: string): ISkeletonColumn => ({
    borderRadius: '4px',
    className,
    hasText: true,
    rowPadding: '7px 0',
    textWidth: '60px',
    type: 'text',
    width: '160px',
  });

  private readonly actionCol = (className: string): ISkeletonColumn => ({
    borderRadius: '100vw',
    className,
    type: 'text',
    width: '0',
  });

  private skeletonWidths(showFollowers: boolean, showAuthentication: boolean): ISkeletonColumnWidths {
    if (showFollowers && showAuthentication) {
      return {
        actionsWidth: 'w-[7%] flex justify-end',
        authenticationWidth: 'w-[15%]',
        channelWidth: 'w-[38%]',
        followersWidth: 'w-[20%]',
        platformWidth: 'w-[20%]',
      };
    }

    if (showFollowers) {
      return {
        actionsWidth: 'w-[8.3%] flex justify-end',
        channelWidth: 'w-[44.7%]',
        followersWidth: 'w-[23.5%]',
        platformWidth: 'w-[23.5%]',
      };
    }

    if (showAuthentication) {
      return {
        actionsWidth: 'w-[8.75%] flex justify-end',
        authenticationWidth: 'w-[18.75%]',
        channelWidth: 'w-[47.5%]',
        platformWidth: 'w-[25%]',
      };
    }

    return {
      actionsWidth: 'w-[10.77%] flex justify-end',
      channelWidth: 'w-[58.46%]',
      platformWidth: 'w-[30.77%]',
    };
  }

  public readonly skeletonPlatformColumns = computed<ISkeletonColumn[]>(() => {
    const widths = this.skeletonWidths(
      this.hasCreatorFollowersReadPermission(),
      this.hasCreatorAuthenticatePermission(),
    );

    return [
      this.iconCol(widths.platformWidth),
      this.iconCol(widths.channelWidth),
      ...(widths.followersWidth !== undefined ? [this.textCol(widths.followersWidth)] : []),
      ...(widths.authenticationWidth !== undefined ? [this.textCol(widths.authenticationWidth)] : []),
      this.actionCol(widths.actionsWidth),
    ];
  });

  public readonly skeletonPlatformRows = computed(() => (this.creator()?.channels?.length || 0) + 1);
  public readonly loadedChannels = computed(() => !this.loading());
  public readonly instagramAuthUrl = computed(() => SocialUtilitiesService.getInstagramAuthUrl());

  public readonly isLoading = signal<boolean>(false);
  public readonly useOptionClicked = signal<boolean>(false);

  public readonly EPlatform = EPlatform;

  public readonly store = inject(CreatorPlatformListStore);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly eventTrackingService = inject(EventTrackingService);

  constructor() {
    super();

    effect(() => {
      if (!this.creator()) {
        return;
      }

      this.store.setInitialChannels(this.creator().channels ?? []);
      this.loadCreatorAnalytics();
    });

    effect(() => {
      if (!this.creator()) {
        return;
      }

      this.updateDataSource();
    });

    effect(() => {
      if (this.store.refreshedChannel()) {
        this.updateDataSource();
        this.store.refreshedChannel.set(null);
      }
    });

    effect(() => {
      if (this.store.channels$()) {
        this.channelsChanged.emit(this.store.channels$());
      }

      this.emitCreatorChanged();
    });

    effect(() => {
      if (!this.loading()) {
        this.useOptionClicked.set(false);
      }
    });

    this.setupLoadingEffect();

    this.searchControl.valueChanges.subscribe((value) => {
      this.searchValue.set(value ?? '');
    });

    this.setupFilter();
  }

  private setupFilter(): void {
    this.dataSource().filterPredicate = (data: IChannel, searchFilter: string): boolean => {
      if (!searchFilter || searchFilter.trim() === '') {
        return true;
      }

      const searchTerm = searchFilter.toLowerCase().trim();
      const platformName = String(this.translate.instant(`shared.platformFeatures.${data.platform}`)).toLowerCase();
      const channelName = data.platformCreatorId.toLowerCase();
      const followers = data.followers?.toString() || '';

      return platformName.includes(searchTerm) || channelName.includes(searchTerm) || followers.includes(searchTerm);
    };

    effect(() => {
      const filterValue = this.searchValue();
      this.dataSource().filter = filterValue || '';
    });
  }

  public loadCreatorAnalytics(): void {
    if (!this.creator()?.id) {
      return;
    }

    this.store.fetchCreatorAnalytics({
      adminMode: this.adminMode(),
      creatorId: this.creator().id,
      creatorMode: this.creatorMode(),
    });
  }

  public onDeleteClick(event: Event, channel: IChannel, menuTrigger: MatMenuTrigger): void {
    event.stopPropagation();
    menuTrigger.closeMenu();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.delete'),
        message: this.translate.instant('pages.channels.datatable.deleteChannelMessage', {
          name: channel.platform,
        }),
        title: this.translate.instant('pages.channels.datatable.deleteChannel'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        if (!channel.id) {
          const updatedChannels = this.dataSource().data.filter((ch) => ch !== channel);
          this.dataSource().data = updatedChannels;
          this.channelsChanged.emit(updatedChannels);
        } else if (this.creator()?.id) {
          this.store.deleteChannel({
            creatorMode: this.creatorMode(),
            id: channel.id,
            // Only report the event once the delete actually succeeds, not on the fire-and-forget
            // call below - a failed request leaves the channel in place.
            onDeleted: () => {
              this.eventTrackingService.track(EEventType.creatorChannelDeleted, { creatorId: this.creator()?.id });
            },
          });
        }
      });
  }

  public onUseAsDefaultClick(event: Event, channel: IChannel, menuTrigger: MatMenuTrigger): void {
    event.stopPropagation();
    menuTrigger.closeMenu();
    this.useOptionClicked.set(true);

    if (!channel.iconUrl) {
      return;
    }

    this.channelSelectedAsDefault.emit({ creatorName: channel.platformCreatorId, iconUrl: channel.iconUrl });
  }

  public onUseImageClick(event: Event, channel: IChannel, menuTrigger: MatMenuTrigger): void {
    event.stopPropagation();
    menuTrigger.closeMenu();
    this.useOptionClicked.set(true);

    if (!channel.iconUrl) {
      return;
    }

    this.channelImageSelected.emit(channel.iconUrl);
  }

  public onRefreshImageClick(event: Event, channel: IChannel, menuTrigger: MatMenuTrigger): void {
    event.stopPropagation();
    menuTrigger.closeMenu();

    this.store.refreshChannelIcon({ creatorMode: this.creatorMode(), id: channel.id });
  }

  public onRowClick(channel: IChannel): void {
    const { url } = channel;

    if (!url) {
      return;
    }

    window.open(url, '_blank');
  }

  public onAddChannelClick(): void {
    const data: IAddPlatformDialogData = {
      createInstantly: false,
      creator: this.creator(),
      creatorMode: this.creatorMode(),
      redirectToCreatorDetails: false,
    };

    const dialogRef = this.dialog.open(AddPlatformDialogComponent, {
      autoFocus: true,
      data,
    });

    dialogRef.afterClosed().subscribe((result: IChannel | undefined) => {
      if (result) {
        this.setChannelsAndGetAnalytics(result as IChannel);
      }
    });
  }

  public authenticateClicked(event: Event, menuTrigger: MatMenuTrigger): void {
    event.stopPropagation();
    menuTrigger.closeMenu();

    const instagramAuthUrl = this.instagramAuthUrl();

    if (!instagramAuthUrl) {
      return;
    }

    window.open(instagramAuthUrl, '_blank');
    this.eventTrackingService.track(EEventType.creatorAuthenticationOpened, { creatorId: this.creator()?.id });
  }

  public authenticateGuideClicked(event: Event, menuTrigger: MatMenuTrigger): void {
    event.stopPropagation();
    menuTrigger.closeMenu();

    const instagramAuthGuideUrl = SocialUtilitiesService.getInstagramAuthGuideUrl();

    if (!instagramAuthGuideUrl) {
      return;
    }

    window.open(instagramAuthGuideUrl, '_blank');
  }

  private updateDataSource(): void {
    const channels = this.store.channels$();
    const analyticsResponse = this.store.creatorAnalytics$();

    const analytics = analyticsResponse ? [analyticsResponse] : [];

    const mergedChannels = this.mergeChannelAnalytics(channels, analytics.flat());

    this.dataSource().data = mergedChannels;
    this.updateTotalFollowers(mergedChannels);
  }

  private mergeChannelAnalytics(channels: IChannel[], analytics: ICreatorChannelAnalyticsResponse[]): IChannel[] {
    return channels.map((channel): IChannel => {
      const matchingAnalytics = analytics
        .reduce<IChannelAnalytics[]>((acc, a) => acc.concat(a.channelAnalytics), [])
        .find(
          (ca) =>
            camelCase(ca?.platform ?? '') === camelCase(channel.platform) &&
            ca?.platformTechnicalId === channel.platformTechnicalId,
        );

      if (matchingAnalytics) {
        return {
          ...channel,
          followers: matchingAnalytics.followers,
          platform: camelCase(channel.platform) as EPlatform,
        };
      }

      return { ...channel, platform: camelCase(channel.platform) as EPlatform };
    });
  }

  private updateTotalFollowers(channels: IChannel[]): void {
    const total = channels.reduce((sum, channel) => sum + (channel.followers || 0), 0);

    this.totalFollowers.set(total);
  }

  private emitCreatorChanged(): void {
    const currentChannels = this.creator()?.channels || [];
    const storeChannels = this.store.channels$();

    if (JSON.stringify(currentChannels) !== JSON.stringify(storeChannels)) {
      const updatedCreator = { ...this.creator() };
      updatedCreator.channels = storeChannels;
      this.creatorChanged.emit(updatedCreator);
    }
  }

  private setupLoadingEffect(): void {
    effect(() => {
      const loading = this.store.loading$();

      if (loading) {
        setTimeout(() => {
          this.isLoading.set(true);
        }, 100);
      } else {
        this.isLoading.set(false);
      }
    });
  }

  private setChannelsAndGetAnalytics(newChannel?: IChannel): void {
    const channels = newChannel ? [...(this.creator().channels ?? []), newChannel] : (this.creator().channels ?? []);

    this.store.setInitialChannels(channels);
    this.loadCreatorAnalytics();
  }
}
